import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useInvalidate } from "@/lib/queries";
import { catLabel } from "@/lib/lore";
import { wordDiff } from "@/lib/diff";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const when = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const Diff = ({ from, to, testid }) => {
  const parts = wordDiff(from || "", to || "");
  if (!from && !to) return <p className="text-sm italic text-[#A8A090]">Empty</p>;
  return (
    <p className="whitespace-pre-wrap text-sm leading-relaxed text-[#E8DFD1]" data-testid={testid}>
      {parts.map((p, i) => (p.t === "same" ? <span key={i}>{p.v}</span>
        : <span key={i} className={p.t === "add" ? "rounded-sm bg-[#10B981]/20 text-[#6EE7B7]" : "rounded-sm bg-red-500/15 text-red-300 line-through"}>{p.v}</span>))}
    </p>
  );
};

const Field = ({ label, changed, children }) => (
  <div className="border-b border-[#2D3442] py-3 last:border-0">
    <div className="mb-1.5 flex items-center gap-2"><span className="lf-label">{label}</span>{changed && <span className="text-[10px] uppercase tracking-wider text-[#F59E0B]">changed</span>}</div>
    {children}
  </div>
);

const Compare = ({ v, cur }) => {
  const s = v.snapshot;
  const attrKeys = [...new Set([...Object.keys(cur.attributes || {}), ...Object.keys(s.attributes || {})])];
  const eq = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  return (
    <div data-testid="history-compare">
      <p className="mb-2 text-xs text-[#A8A090]">Restoring brings back <span className="rounded-sm bg-[#10B981]/20 px-1 text-[#6EE7B7]">green</span> and removes <span className="rounded-sm bg-red-500/15 px-1 text-red-300 line-through">red</span>.</p>
      <Field label="Name" changed={!eq(s.name, cur.name)}><Diff from={cur.name} to={s.name} testid="history-diff-name" /></Field>
      <Field label="Category" changed={!eq(s.category, cur.category)}><p className="text-sm text-[#E8DFD1]">{catLabel(s.category)}{!eq(s.category, cur.category) && <span className="text-[#A8A090]"> (now {catLabel(cur.category)})</span>}</p></Field>
      <Field label="Summary" changed={!eq(s.summary, cur.summary)}><Diff from={cur.summary} to={s.summary} testid="history-diff-summary" /></Field>
      <Field label="Notes" changed={!eq(s.content, cur.content)}><Diff from={cur.content} to={s.content} testid="history-diff-content" /></Field>
      {attrKeys.length > 0 && (
        <Field label="Attributes" changed={!eq(s.attributes, cur.attributes)}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {attrKeys.map((k) => (
              <div key={k} className="contents"><dt className="capitalize text-[#A8A090]">{k.replace(/_/g, " ")}</dt><dd><Diff from={String(cur.attributes?.[k] ?? "")} to={String(s.attributes?.[k] ?? "")} /></dd></div>
            ))}
          </dl>
        </Field>
      )}
      <Field label="Also known as" changed={!eq(s.aliases, cur.aliases)}><Diff from={(cur.aliases || []).join(", ")} to={(s.aliases || []).join(", ")} /></Field>
      <Field label="Images" changed={!eq(s.images, cur.images)}><p className="text-sm text-[#E8DFD1]">{(s.images || []).length} image{(s.images || []).length === 1 ? "" : "s"} <span className="text-[#A8A090]">(now {(cur.images || []).length})</span></p></Field>
    </div>
  );
};

export const HistoryDialog = ({ ent, canEdit, onClose }) => {
  const invalidate = useInvalidate();
  const { data, isLoading } = useQuery({ queryKey: ["history", ent.id], queryFn: () => api.get(`/entities/${ent.id}/history`).then((r) => r.data) });
  const [sel, setSel] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const v = data?.versions.find((x) => x.id === sel);

  const restore = async () => {
    try {
      const { data: res } = await api.post(`/entities/${ent.id}/history/${v.id}/restore`);
      toast.success("Earlier version restored", { description: res.name_conflict ? `Kept the name "${res.name_conflict}" because another entry already uses the old one.` : "Your previous text was saved in the history too." });
      invalidate(["entity", ent.id], ["entities", ent.universe_id], ["history", ent.id]);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden p-0" data-testid="history-dialog">
        <div className="flex max-h-[90vh] flex-col">
          <DialogHeader className="border-b border-[#2D3442] p-5">
            <DialogTitle className="font-display text-3xl">History of {ent.name}</DialogTitle>
            <DialogDescription>Each save keeps the version from just before it. Pick one to compare with the current entry.</DialogDescription>
          </DialogHeader>
          <div className="grid min-h-0 flex-1 md:grid-cols-[260px_1fr]">
            <div className={`min-h-0 overflow-y-auto border-[#2D3442] p-2 lf-scroll md:border-r ${sel ? "hidden md:block" : ""}`} data-testid="history-list">
              <div className="rounded px-3 py-2 text-sm">
                <div className="text-[#F3EBDD]">Current version</div>
                <div className="text-[11px] text-[#A8A090]">{data?.updated_at ? when(data.updated_at) : ""}</div>
              </div>
              {isLoading && <p className="px-3 py-2 text-sm text-[#A8A090]">Loading…</p>}
              {data && data.versions.length === 0 && <p className="px-3 py-2 text-sm text-[#A8A090]" data-testid="history-empty">No earlier versions yet. They appear after the next edit.</p>}
              {data?.versions.map((x) => (
                <button key={x.id} onClick={() => setSel(x.id)} data-testid={`history-version-${x.id}`}
                  className={`w-full rounded px-3 py-2 text-left transition-colors hover:bg-[#1A202C] ${sel === x.id ? "bg-[#1A202C] shadow-[inset_2px_0_0_#D97706]" : ""}`}>
                  <div className="text-sm text-[#E8DFD1]">{when(x.created_at)}</div>
                  <div className="truncate text-[11px] text-[#A8A090]">{x.reason} · {x.by_name}</div>
                </button>
              ))}
            </div>
            <div className={`min-h-0 overflow-y-auto p-5 lf-scroll ${sel ? "" : "hidden md:block"}`}>
              {!v ? <p className="text-sm text-[#A8A090]">Select a version on the left.</p> : (
                <>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="ghost" className="md:hidden" onClick={() => setSel(null)} data-testid="history-back"><ArrowLeft className="mr-1 h-4 w-4" />Versions</Button>
                    <div className="order-last w-full text-sm text-[#A8A090] sm:order-none sm:w-auto sm:min-w-0 sm:flex-1">{when(v.created_at)} · {v.reason} · {v.by_name}</div>
                    {canEdit && <Button size="sm" className="ml-auto sm:ml-0" onClick={() => setConfirm(true)} data-testid="history-restore-button"><RotateCcw className="mr-1.5 h-4 w-4" />Restore this version</Button>}
                  </div>
                  <Compare v={v} cur={data.current} />
                </>
              )}
            </div>
          </div>
        </div>
        <ConfirmDialog open={confirm} onOpenChange={setConfirm} onConfirm={restore} title="Restore this version?"
          description="The entry will go back to how it was then. The current version is kept in the history, so you can undo this." />
      </DialogContent>
    </Dialog>
  );
};
